import $ from 'jquery';

/**
 * Directives lack state, keep track of the 'current' tooltip and whether it should be displayed per element to enable changing content
 * @type {WeakMap<HTMLElement, { isEnabled: Boolean, tooltip: JourneyBuilder.Tooltip }>}
 */
const tooltipDataPerElement = new WeakMap();

/**
 * Verify whether the given content is a valid tooltip display value
 * 
 * @param content
 * @return {Boolean}
 */
function isValidDisplayValue(content){
    return content !== undefined && content !== null;
}

/**
 * Ensure the given value is a valid tooltip display value
 * 
 * @param {T} content
 * @return {T | String}
 * 
 * @template T
 */
function toDisplayableValue(content) {
    if(!isValidDisplayValue(content)){
        return '';
    }
    
    return content;
}
    
/**
 * Check whether a tooltip may be rendered for the given element
 * 
 * A tooltip may be displayed when tooltips are enabled, and the element is hovered
 * @param {HTMLElement} el
 * @return {Boolean}
 */
function canDisplayTooltip(el){
    return tooltipDataPerElement.get(el).isEnabled &&
        el.matches(':hover');
}

/**
 * When enabled, display the tooltip associated with the element
 * @param {HTMLElement} el
 * @return {Promise<void>}
 */
async function displayTooltip(el) {
    if(!canDisplayTooltip(el)){
        return;
    }

    //We assume our element has tooltipData at this stage
    const {tooltip} = tooltipDataPerElement.get(el);
    
    const {
        title,
        content: contentAwaitable
    } = tooltip;

    //contentAwaitable can be either a string, or a promise
    //both are awaitable, non-'thennable' objects simply resolve immediately
    const content = await contentAwaitable;
    
    if(!isValidDisplayValue(title) && !isValidDisplayValue(content)){
        return;
    }

    const $el = $(el);
    
    //We can't make any assumptions about the time it takes for our function to complete
    //attempting to remove an existing tooltip prior to display should be our safest bet
    //regarding changes that might've taken place while our current promise was still computing
    destroyDisplayedTooltip($el);

    //Our tooltip may not be displayable anymore post await
    if(!canDisplayTooltip(el)){
        return;
    }
    
    $el.popover({
        //Title/Content may not be undefined/null
        title: toDisplayableValue(title),
        content: toDisplayableValue(content),
        trigger: 'hover',
        placement: 'auto',
        boundary: 'viewport',
        html: true
    });
    
    $el.popover('show');
}

/**
 * Destroy the tooltip associated with the element
 * @param {HTMLElement} el
 */
function destroyDisplayedTooltip(el){
    const $el = $(el);

    //If applicable, ensure currently displayed tooltip is removed
    $el.popover('dispose'); 

    //Remove tooltip plugin from element, allow redefining our tooltip content
    $el.removeData('bs.popover');
}

/**
 * Vue directive used for displaying {JourneyBuilder.Tooltip} objects
 * 
 * Directive expects `value` to be boolean, indicating whether or not the tooltip is to be displayed
 * 
 * Directive expects `arg` to be a {JourneyBuilder.Tooltip} object, containing the content to be displayed
 */
const tooltipped = {
    bind: (el, binding, vnode) => {
        tooltipDataPerElement.set(el, { isEnabled: binding.value !== false, tooltip: binding.arg });
        
        el.addEventListener('mouseenter', () => displayTooltip(el));
        el.addEventListener('mouseleave', () => destroyDisplayedTooltip(el));
    },
    inserted: (el, binding, vnode) => {},
    update: (el, binding, vnode, oldVnode) => {
        tooltipDataPerElement.set(el, { isEnabled: binding.value !== false, tooltip: binding.arg });

        //Cleanup previous tooltip
        destroyDisplayedTooltip(el);
        
        //Attempt to display tooltip again
        // noinspection JSIgnoredPromiseFromCall
        displayTooltip(el);
    },
    componentUpdated: (el, binding, vnode, oldVnode) => {},
    unbind: (el, binding, vnode) => {}
}

export default tooltipped;